"use client";

import * as React from "react";
import { ArrowRight } from "lucide-react";

import {
  AddToCartButton,
  EcommerceProductsProvider,
  ProductSortDropdown,
  ProductsFilter,
  useEcommerceProducts,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    total,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage,
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(6)].map((_, i) => (
            <div key={i} data-index={i} className="space-y-4">
              <div className="aspect-square rounded-lg bg-muted/40 animate-pulse" />
              <div className="h-3 w-20 bg-muted/40 rounded animate-pulse" />
              <div className="h-5 w-40 bg-muted/40 rounded animate-pulse" />
              <div className="h-px bg-border" />
              <div className="h-5 w-16 bg-muted/40 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-muted-foreground py-12 text-center">
        Failed to load our flavors.{" "}
        <Button variant="link" onClick={refetch} className="px-1 italic">
          Retry
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="py-20 text-center">
        <p className="font-default text-xl text-foreground">No flavors match your selection.</p>
        <p className="mt-2 text-sm text-muted-foreground italic">Try adjusting your filters.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.map((product, i) => (
          <article
            data-index={i}
            key={product.id}
            className="group flex flex-col"
          >
            <Link to={product.link} className="block">
              <div className="aspect-square overflow-hidden rounded-lg bg-accent/40">
                {product.featured_image_url && (
                  <img
                    src={product.featured_image_url}
                    alt={product.title}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>
            </Link>

            <div className="mt-4">
              {product.categories && product.categories.length > 0 && (
                <p
                  className="text-[0.7rem] font-default uppercase tracking-widest"
                  style={{ color: "var(--chart-1)" }}
                >
                  {product.categories[0].name}
                </p>
              )}

              <Link to={product.link}>
                <h3 className="font-default mt-1 text-xl text-foreground leading-snug">
                  {product.title}
                </h3>
              </Link>

              <div className="mt-3 h-px w-full bg-border" />

              <div className="mt-3 flex items-baseline justify-between">
                <div className="font-default text-lg text-foreground">
                  {product.discounted_price !== undefined &&
                  product.discounted_price !== null &&
                  product.discounted_price > 0 ? (
                    <span className="flex items-baseline gap-2">
                      <span>${product.discounted_price}</span>
                      <span className="text-sm text-muted-foreground line-through">
                        ${product.price}
                      </span>
                    </span>
                  ) : (
                    <span>${product.price}</span>
                  )}
                </div>

                <Link
                  to={product.link}
                  className="font-default text-sm italic text-foreground opacity-0 transition-opacity duration-300 group-hover:opacity-100 inline-flex items-center gap-1"
                >
                  View details
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <div className="mt-4">
                <AddToCartButton
                  productId={product.id}
                  variant="outline"
                  size="sm"
                  className="w-full"
                  buttonClassName="bg-primary text-primary-foreground rounded-lg"
                  text="Add to scoop"
                  showQuantity={false}
                />
              </div>
            </div>
          </article>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-16">
          <Pagination>
            <PaginationContent className="gap-2">
              <PaginationItem>
                <PaginationPrevious
                  onClick={(e) => {
                    e.preventDefault();
                    if (hasPrev) prevPage();
                  }}
                  className={`rounded-lg border border-border ${
                    !hasPrev ? "pointer-events-none opacity-40" : "cursor-pointer"
                  }`}
                />
              </PaginationItem>

              {Array.from({ length: totalPages }).map((_, idx) => {
                const page = idx + 1;
                const isActive = page === currentPage;
                const showPage =
                  page === 1 ||
                  page === totalPages ||
                  Math.abs(page - currentPage) <= 1;

                if (!showPage) {
                  if (page === 2 || page === totalPages - 1) {
                    return (
                      <PaginationItem key={`ellipsis-${page}`} data-index={idx}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  return null;
                }

                return (
                  <PaginationItem key={page} data-index={idx}>
                    <PaginationLink
                      isActive={isActive}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(page);
                      }}
                      className={`rounded-lg border border-border cursor-pointer font-default ${
                        isActive
                          ? "bg-primary text-primary-foreground border-primary"
                          : ""
                      }`}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  onClick={(e) => {
                    e.preventDefault();
                    if (hasNext) nextPage();
                  }}
                  className={`rounded-lg border border-border ${
                    !hasNext ? "pointer-events-none opacity-40" : "cursor-pointer"
                  }`}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}

export default function Shop() {
  const current_wp_query_params =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section data-section-id="2648" id="shop-content" className="bg-background text-foreground py-20 lg:pt-20">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col lg:flex-row lg:gap-[60px]">
            <aside className="hidden lg:block lg:w-1/4 shrink-0">
              <div className="sticky top-[120px] max-h-[calc(100vh-140px)] overflow-y-auto pr-6 border-r border-border">
                <div className="pb-2">
                  <p
                    className="text-[0.7rem] font-default uppercase tracking-widest mb-6"
                    style={{ color: "var(--chart-1)" }}
                  >
                    Filter selection
                  </p>
                  <ProductsFilter />
                </div>
              </div>
            </aside>

            <div className="lg:hidden mb-8">
              <details className="rounded-lg border border-border bg-card/40">
                <summary className="cursor-pointer px-4 py-3 font-default text-sm text-foreground">
                  Refine selection
                </summary>
                <div className="px-4 pb-4 pt-2">
                  <ProductsFilter />
                </div>
              </details>
            </div>

            <div className="w-full lg:w-3/4">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-10">
                <p className="font-default text-sm text-muted-foreground tracking-wide">
                  Artisanal collection
                </p>
                <ProductSortDropdown label="Sort by" />
              </div>
              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}